// Shared layouts
import { Dashboard as DashboardLayout } from "layouts";
import React, { useState, useEffect } from "react";

import { storage, firestore_db } from "../../config/firebase_config";

const OtherApps = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newApp, setNewApp] = useState({
    name: "",
    icon: null,
    appFile: null,
    package_name: "",
    status: true,
  });

  const totalPages = Math.max(1, Math.ceil(apps.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentApps = apps.slice(startIndex, endIndex);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [apps, itemsPerPage, currentPage, totalPages]);
  useEffect(() => {
    fetchApps();
  }, []);

  const fetchApps = async () => {
    try {
      setLoading(true);

      const snapshot = await firestore_db.collection("others_apps").get();

      const appsData = snapshot.docs.map((doc) => {
        const data = doc.data();

        return {
          id: doc.id,
          name: data.name || "",
          icon: data.icon || "",
          fileurl: data.fileurl || "",
          package_name: data.package_name || "",
          status: data.status || true,
          created_at: data.created_at || null,
        };
      });

      // Optional: sort latest first
      appsData.sort(
        (a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0),
      );

      setApps(appsData);
    } catch (error) {
      console.error("Error fetching apps:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddApp = async () => {
    try {
      if (!newApp.name || !newApp.package_name) {
        alert("Please fill required fields");
        return;
      }

      // Optional loading state recommended
      setLoading(true);

      const iconUrl = await uploadFile(newApp.icon, "icons");
      const fileurl = await uploadFile(newApp.fileurl, "apks");

      await firestore_db.collection("others_apps").add({
        name: newApp.name,
        package_name: newApp.package_name,
        icon: iconUrl,
        fileurl: fileurl,
        status: true,
        created_at: new Date(),
      });

      // Refresh list after add
      const snapshot = await firestore_db.collection("others_apps").get();

      const appsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setApps(appsData);

      // Reset form
      setNewApp({
        name: "",
        icon: null,
        appFile: null,
        package_name: "",
        status: true,
      });

      setShowAddDialog(false);

      alert("App added successfully 🚀");
    } catch (error) {
      console.error("Upload error:", error);
      alert("Something went wrong");
    } finally {
      setLoading(false); // STOP loader
    }
  };

  const handleDeleteApp = async (app) => {
    const confirmDelete = window.confirm(`Delete "${app.name}" permanently?`);

    if (!confirmDelete) return;

    try {
      setLoading(true);
      // ✅ Delete Icon
      if (app.icon) {
        const iconRef = storage.refFromURL(app.icon);
        await iconRef.delete().catch(() => {});
      }
      // ✅ Delete APK
      if (app.fileurl) {
        const fileRef = storage.refFromURL(app.fileurl);
        await fileRef.delete().catch(() => {});
      }

      // ✅ Delete Firestore Doc
      console.log(app.id);
      await firestore_db
        .collection("others_apps")
        .doc(app.id)
        .delete();

      // ✅ Update UI
      setApps((prev) => prev.filter((item) => item.id !== app.id));

      alert("App deleted successfully 🚀");
      fetchApps();
    } catch (error) {
      console.error("Delete error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditApp = (index) => {
    const actualIndex = startIndex + index;
    setEditingApp({ ...apps[actualIndex], index: actualIndex });
    setShowEditDialog(true);
  };

  const handleUpdateApp = async () => {
    if (!editingApp?.id) return;

    try {
      // Optional loading state
      setLoading(true);

      let iconUrl = editingApp.icon;
      let fileUrl = editingApp.fileurl;

      // ✅ If new icon selected
      if (editingApp.newIconFile) {
        iconUrl = await uploadFile(editingApp.newIconFile, "icons");

        // delete old icon
        if (editingApp.icon) {
          const iconRef = storage.refFromURL(editingApp.icon);
          await iconRef.delete().catch(() => {});
        }
      }

      // ✅ If new APK selected
      if (editingApp.newApkFile instanceof File) {
        fileUrl = await uploadFile(editingApp.newApkFile, "apks");

        // delete old file
        if (editingApp.fileurl) {
          const fileRef = storage.refFromURL(editingApp.fileurl);
          await fileRef.delete().catch(() => {});
        }
      }

      // ✅ Update Firestore
      await firestore_db
        .collection("others_apps")
        .doc(editingApp.id)
        .update({
          name: editingApp.name,
          package_name: editingApp.package_name,
          icon: iconUrl,
          fileurl: fileUrl,
          status: editingApp.status,
          updatedAt: new Date(),
        });

      // ✅ Update UI without refetch
      setApps((prev) =>
        prev.map((app) =>
          app.id === editingApp.id
            ? { ...app, ...editingApp, icon: iconUrl, fileurl: fileUrl }
            : app,
        ),
      );

      setShowEditDialog(false);
      setEditingApp(null);

      alert("✅ App updated successfully");
    } catch (error) {
      console.error("Update error:", error);
      alert("❌ Failed to update app");
    } finally {
      setLoading(false);
    }
  };

  const handleEditIconUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setEditingApp((prev) => ({
      ...prev,
      newIconFile: file, // ✅ store file for upload
      iconPreview: URL.createObjectURL(file), // preview only
    }));
  };

  const handleEditFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith(".apk")) {
      alert("Only APK files allowed");
      return;
    }
    // 🔥 Optional protection
    if (file.size > 100 * 1024 * 1024) {
      alert("APK must be under 100MB");
      return;
    }

    setEditingApp((prev) => ({
      ...prev,
      newApkFile: file, // ✅ store FILE only
    }));
  };
  useEffect(() => {
    return () => {
      if (editingApp?.iconPreview) {
        URL.revokeObjectURL(editingApp.iconPreview);
      }
    };
  }, [editingApp]);
  const handleIconUpload = (e) => {
    const file = e.target.files[0]; // ✅ THIS is the real file

    if (!file) return;

    setNewApp({
      ...newApp,
      icon: file, // store FILE
      iconPreview: URL.createObjectURL(file), // preview only
    });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewApp({ ...newApp, fileurl: file, filePreview: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };
  const uploadFile = async (file, folder) => {
    if (!file) return null;
    return new Promise((resolve, reject) => {
      if (!file) return reject("No file selected");

      // ✅ Create unique name (VERY IMPORTANT)
      const fileName = `others_apps/${folder}/${Date.now()}_${file.name}`;

      const uploadTask = storage.ref(fileName).put(file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;

          console.log("Upload is " + progress.toFixed(0) + "% done");

          // 👉 If you have progress state:
          // setUploadProgress(progress);
        },
        (error) => {
          console.error("Upload error:", error);
          reject(error);
        },
        async () => {
          const downloadURL = await uploadTask.snapshot.ref.getDownloadURL();

          resolve(downloadURL);
        },
      );
    });
  };
  const getPageNumbers = () => {
    const delta = 2;
    const pages = [];

    for (
      let i = Math.max(1, currentPage - delta);
      i <= Math.min(totalPages, currentPage + delta);
      i++
    ) {
      pages.push(i);
    }

    if (!pages.includes(1)) {
      pages.unshift(1);
      if (pages[1] !== 2) pages.splice(1, 0, "...");
    }

    if (!pages.includes(totalPages)) {
      if (pages[pages.length - 1] !== totalPages - 1) {
        pages.push("...");
      }
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <DashboardLayout title="Other Apps">
      <div
        style={{
          minHeight: "100vh",
          backgroundColor: "#f5f5f5",
          padding: "40px",
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        }}
      >
        <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
          {/* Header */}
          <div
              style={{
                display: "flex",
                justifyContent: "flex-end", // ✅ pushes button right
                alignItems: "center",
                marginBottom: "30px",
              }}
            >
              {/* Add New Button */}

              <button
                onClick={() => setShowAddDialog(true)}
                style={{
                  padding: "12px 24px",
                  backgroundColor: "#3b82f6",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "background-color 0.2s",
                }}
              >
                <span style={{ fontSize: "18px" }}>+</span> Add New
              </button>
            </div>

          {/* Table Container */}
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
            }}
          >
            {/* Table Wrapper with Horizontal Scroll */}
            <div
              style={{
                overflowX: "auto",
                overflowY: "visible",
              }}
            >
              {/* Table */}
              <table
                style={{
                  width: "100%",
                  minWidth: "1000px",
                  borderCollapse: "collapse",
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#f7fafc" }}>
                    <th style={headerStyle}>Icon</th>
                    <th style={headerStyle}>Name</th>
                    <th style={headerStyle}>Package Name</th>
                    <th style={headerStyle}>File URL</th>
                    <th style={headerStyle}>Status</th>
                    <th style={headerStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentApps.map((app, index) => (
                    <tr
                      key={index}
                      style={{
                        borderBottom: "1px solid #e2e8f0",
                        transition: "background-color 0.2s",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = "#f7fafc")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = "white")
                      }
                    >
                      <td style={cellStyle}>
                        <img
                          src={app.icon}
                          alt="App Icon"
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.src =
                              "https://cdn-icons-png.flaticon.com/512/888/888857.png";
                          }}
                          style={{
                            width: "42px",
                            height: "42px",
                            borderRadius: "10px",
                            objectFit: "cover",
                            display: "block",
                            backgroundColor: "#ffffff",
                            border: "1px solid #e5e7eb",
                            opacity: 1, // 👈 force full visibility
                            filter: "none", // 👈 remove grayscale
                          }}
                        />
                      </td>
                      <td style={cellStyle}>{app.name}</td>
                      <td style={cellStyle}>{app.package_name}</td>
                      <td style={cellStyle}>
                        {app.fileurl && (
                          <a
                            href={app.fileurl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              background: "#eff6ff",
                              padding: "8px",
                              borderRadius: "8px",
                              fontSize: "18px",
                              textDecoration: "none",
                            }}
                            title="Download APK"
                          >
                            📥
                          </a>
                        )}
                      </td>

                      <td style={cellStyle}>
                        <span
                          style={{
                            padding: "6px 16px",
                            borderRadius: "6px",
                            fontSize: "13px",
                            fontWeight: "500",
                            backgroundColor:
                              app.status === true ? "#c6f6d5" : "#fee2e2",
                            color: app.status === true ? "#22543d" : "#991b1b",
                            display: "inline-block",
                          }}
                        >
                          {app.status === true ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td style={cellStyle}>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            justifyContent: "center",
                          }}
                        >
                          <button
                            onClick={() => handleEditApp(index)}
                            style={actionButtonStyle}
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDeleteApp(app)}
                            style={{
                              ...actionButtonStyle,
                              backgroundColor: "#fee2e2",
                            }}
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "20px 30px",
                backgroundColor: "#f7fafc",
                borderTop: "1px solid #e2e8f0",
              }}
            >
              {/* Left side pagination controls */}
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  style={paginationButtonStyle(currentPage === 1)}
                >
                  ⏮
                </button>
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  style={paginationButtonStyle(currentPage === 1)}
                >
                  ◀
                </button>

                {getPageNumbers().map((page, index) =>
                  page === "..." ? (
                    <span key={index} style={{ padding: "8px" }}>
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      style={{
                        ...paginationButtonStyle(false),
                        backgroundColor:
                          currentPage === page ? "#fef3c7" : "white",
                        color: currentPage === page ? "#92400e" : "#4a5568",
                        fontWeight: currentPage === page ? "600" : "500",
                      }}
                    >
                      {page}
                    </button>
                  ),
                )}

                <button
                  onClick={() =>
                    setCurrentPage(Math.min(totalPages, currentPage + 1))
                  }
                  disabled={currentPage === totalPages}
                  style={paginationButtonStyle(currentPage === totalPages)}
                >
                  ▶
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  style={paginationButtonStyle(currentPage === totalPages)}
                >
                  ⏭
                </button>
              </div>

              {/* Center - Items per page */}
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: "8px 12px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                    fontSize: "14px",
                    backgroundColor: "white",
                    cursor: "pointer",
                    outline: "none",
                  }}
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                </select>
                <span style={{ fontSize: "14px", color: "#718096" }}>
                  Items per page
                </span>
              </div>
            </div>
          </div>

          {/* Add New Dialog */}
          {showAddDialog && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start", // ✅ allows scroll
                padding: "20px",
                overflowY: "auto", // ✅ scroll entire modal
                zIndex: 1000,
              }}
            >
              <div
                style={{
                  backgroundColor: "white",
                  borderRadius: "12px",
                  padding: "30px",
                  width: "500px",
                  maxWidth: "95%",
                  maxHeight: "90vh",
                  overflowY: "auto",
                  boxShadow:
                    "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                }}
              >
                <h2
                  style={{
                    fontSize: "24px",
                    fontWeight: "600",
                    color: "#2d3748",
                    marginBottom: "20px",
                  }}
                >
                  Add New App
                </h2>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#4a5568",
                        marginBottom: "6px",
                      }}
                    >
                      App Name *
                    </label>
                    <input
                      type="text"
                      value={newApp.name}
                      onChange={(e) =>
                        setNewApp({ ...newApp, name: e.target.value })
                      }
                      placeholder="e.g., WhatsApp"
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        border: "1px solid #e2e8f0",
                        borderRadius: "6px",
                        fontSize: "14px",
                        outline: "none",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#4a5568",
                        marginBottom: "6px",
                      }}
                    >
                      Package Name *
                    </label>
                    <input
                      type="text"
                      value={newApp.package_name}
                      onChange={(e) =>
                        setNewApp({ ...newApp, package_name: e.target.value })
                      }
                      placeholder="e.g., com.whatsapp"
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        border: "1px solid #e2e8f0",
                        borderRadius: "6px",
                        fontSize: "14px",
                        outline: "none",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#4a5568",
                        marginBottom: "6px",
                      }}
                    >
                      Icon (Image File)
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleIconUpload}
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        border: "1px solid #e2e8f0",
                        borderRadius: "6px",
                        fontSize: "14px",
                        outline: "none",
                        cursor: "pointer",
                      }}
                    />
                    {newApp.icon && (
                      <div style={{ marginTop: "8px", textAlign: "center" }}>
                        <img
                          src={newApp.icon}
                          alt="Icon preview"
                          style={{
                            maxWidth: "64px",
                            maxHeight: "64px",
                            borderRadius: "6px",
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#4a5568",
                        marginBottom: "6px",
                      }}
                    >
                      App File
                    </label>
                    <input
                      type="file"
                      accept=".apk"
                      onChange={handleFileUpload}
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        border: "1px solid #e2e8f0",
                        borderRadius: "6px",
                        fontSize: "14px",
                        outline: "none",
                        cursor: "pointer",
                      }}
                    />
                    {newApp.fileurl && (
                      <div
                        style={{
                          marginTop: "8px",
                          fontSize: "12px",
                          color: "#718096",
                        }}
                      >
                        ✓ File uploaded successfully
                      </div>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    marginTop: "24px",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    onClick={() => setShowAddDialog(false)}
                    style={{
                      padding: "10px 20px",
                      backgroundColor: "#e2e8f0",
                      color: "#4a5568",
                      border: "none",
                      borderRadius: "6px",
                      fontSize: "14px",
                      fontWeight: "500",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddApp}
                    style={{
                      padding: "10px 20px",
                      backgroundColor: "#3b82f6",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      fontSize: "14px",
                      fontWeight: "500",
                      cursor: "pointer",
                    }}
                  >
                    Add App
                  </button>
                </div>
              </div>
              {loading && (
                <div
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backdropFilter: "blur(6px)",
                    backgroundColor: "rgba(0,0,0,0.3)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 2000,
                  }}
                >
                  <div
                    style={{
                      width: "60px",
                      height: "60px",
                      border: "6px solid #e5e7eb",
                      borderTop: "6px solid #3b82f6",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Edit Dialog */}
          {showEditDialog && editingApp && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start", // ✅ allows scroll
                padding: "20px",
                overflowY: "auto", // ✅ scroll entire modal
                zIndex: 1000,
              }}
            >
              <div
                style={{
                  backgroundColor: "white",
                  borderRadius: "12px",
                  padding: "30px",
                  width: "500px",
                  maxWidth: "95%",
                  maxHeight: "90vh",
                  overflowY: "auto",
                  boxShadow:
                    "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                }}
              >
                <h2
                  style={{
                    fontSize: "24px",
                    fontWeight: "600",
                    color: "#2d3748",
                    marginBottom: "20px",
                  }}
                >
                  Edit App
                </h2>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#4a5568",
                        marginBottom: "6px",
                      }}
                    >
                      Icon (Image File)
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleEditIconUpload}
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        border: "1px solid #e2e8f0",
                        borderRadius: "6px",
                        fontSize: "14px",
                        outline: "none",
                        cursor: "pointer",
                      }}
                    />

                    {editingApp.icon && (
                      <div style={{ marginTop: "8px", textAlign: "center" }}>
                        {
                          <img
                            src={editingApp.icon}
                            alt="App Icon"
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.src =
                                "https://cdn-icons-png.flaticon.com/512/888/888857.png";
                            }}
                            style={{
                              width: "42px",
                              height: "42px",
                              borderRadius: "10px",
                              objectFit: "cover",
                              display: "block",
                              backgroundColor: "#ffffff",
                              border: "1px solid #e5e7eb",
                              opacity: 1, // 👈 force full visibility
                              filter: "none", // 👈 remove grayscale
                            }}
                          />
                        }
                      </div>
                    )}
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#4a5568",
                        marginBottom: "6px",
                      }}
                    >
                      App Name *
                    </label>
                    <input
                      type="text"
                      value={editingApp.name}
                      onChange={(e) =>
                        setEditingApp({ ...editingApp, name: e.target.value })
                      }
                      placeholder="e.g., WhatsApp"
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        border: "1px solid #e2e8f0",
                        borderRadius: "6px",
                        fontSize: "14px",
                        outline: "none",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#4a5568",
                        marginBottom: "6px",
                      }}
                    >
                      Package Name *
                    </label>
                    <input
                      type="text"
                      value={editingApp.package_name}
                      onChange={(e) =>
                        setEditingApp({
                          ...editingApp,
                          package_name: e.target.value,
                        })
                      }
                      placeholder="e.g., com.whatsapp"
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        border: "1px solid #e2e8f0",
                        borderRadius: "6px",
                        fontSize: "14px",
                        outline: "none",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#4a5568",
                        marginBottom: "6px",
                      }}
                    >
                      App File (APK)
                    </label>
                    <input
                      type="file"
                      accept=".apk"
                      onChange={handleEditFileUpload}
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        border: "1px solid #e2e8f0",
                        borderRadius: "6px",
                        fontSize: "14px",
                        outline: "none",
                        cursor: "pointer",
                      }}
                    />
                    {editingApp.fileurl && (
                      <div
                        style={{
                          marginTop: "8px",
                          fontSize: "12px",
                          color: "#718096",
                        }}
                      >
                        {editingApp.fileurl.startsWith("data:")
                          ? "✓ Local file attached"
                          : "✓ URL set"}
                      </div>
                    )}
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#4a5568",
                        marginBottom: "6px",
                      }}
                    >
                      Status
                    </label>
                    <select
                      value={editingApp.status}
                      onChange={(e) =>
                        setEditingApp({ ...editingApp, status: e.target.value })
                      }
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        border: "1px solid #e2e8f0",
                        borderRadius: "6px",
                        fontSize: "14px",
                        outline: "none",
                        cursor: "pointer",
                      }}
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    marginTop: "24px",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    onClick={() => {
                      setShowEditDialog(false);
                      setEditingApp(null);
                    }}
                    style={{
                      padding: "10px 20px",
                      backgroundColor: "#e2e8f0",
                      color: "#4a5568",
                      border: "none",
                      borderRadius: "6px",
                      fontSize: "14px",
                      fontWeight: "500",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateApp}
                    style={{
                      padding: "10px 20px",
                      backgroundColor: "#3b82f6",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      fontSize: "14px",
                      fontWeight: "500",
                      cursor: "pointer",
                    }}
                  >
                    Update App
                  </button>
                </div>
              </div>
              {loading && (
                <div
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backdropFilter: "blur(6px)",
                    backgroundColor: "rgba(0,0,0,0.3)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 2000,
                  }}
                >
                  <div
                    style={{
                      width: "60px",
                      height: "60px",
                      border: "6px solid #e5e7eb",
                      borderTop: "6px solid #3b82f6",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

const headerStyle = {
  textAlign: "left",
  padding: "18px 20px",
  fontSize: "13px",
  fontWeight: "600",
  color: "#718096",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const cellStyle = {
  padding: "20px",
  fontSize: "14px",
  color: "#4a5568",
};

const actionButtonStyle = {
  padding: "10px",
  backgroundColor: "#e2e8f0",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#4a5568",
  transition: "background-color 0.2s",
  ":hover": {
    backgroundColor: "#cbd5e0",
  },
};

const paginationButtonStyle = (disabled) => ({
  padding: "8px 14px",
  backgroundColor: disabled ? "#f7fafc" : "white",
  border: "1px solid #e2e8f0",
  borderRadius: "6px",
  cursor: disabled ? "not-allowed" : "pointer",
  fontSize: "14px",
  color: disabled ? "#cbd5e0" : "#4a5568",
  minWidth: "40px",
  transition: "background-color 0.2s",
});

export default OtherApps;
